import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Notification.css';
import { 
  FaBell, 
  FaUser,
  FaCalendarAlt,
  FaExclamationCircle,
  FaInfoCircle
} from 'react-icons/fa';

const Notification = () => {
  const navigate = useNavigate();
  const [todayNotifications, setTodayNotifications] = useState([]);
  const [summaryNotifications, setSummaryNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewedNotifications, setViewedNotifications] = useState(new Set());
  const dropdownRef = useRef(null);

  // Mark notifications as viewed when dropdown opens
  useEffect(() => {
    if (showDropdown) {
      const allIds = [
        ...todayNotifications.map(n => n.id),
        ...summaryNotifications.map(n => n.id)
      ];
      setViewedNotifications(prev => new Set([...prev, ...allIds]));
    }
  }, [showDropdown, todayNotifications, summaryNotifications]);

  const isToday = useCallback((dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    const date = new Date(dateStr);
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Authentication token not found');

      const apiUrl = process.env.REACT_APP_API_URL || 'https://crmapi.credexon.com';
      const res = await axios.get(`${apiUrl}/leads/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const leads = res.data || [];

      // Process today's leads
      const todayLeads = leads.filter(lead =>
        isToday(lead.createdAt) &&
        !['converted', 'disqualified'].includes(lead.status)
      ).map(lead => ({
        id: lead._id,
        message: `${lead.assignedTo?.name || 'Unknown'}: ${lead.name || 'Unnamed'} (${lead.status})`,
        createdAt: lead.createdAt,
        status: lead.status,
        leadId: lead._id
      }));

      setTodayNotifications(todayLeads);

      // Process user summaries
      const grouped = {};
      leads.forEach(lead => {
        if (!lead.assignedTo || !lead.assignedTo.name) return;
        const assignee = lead.assignedTo.name;
        const status = lead.status;
        if (['converted', 'disqualified'].includes(status)) return;

        if (!grouped[assignee]) {
          grouped[assignee] = {
            total: 0,
            statusCount: {}
          };
        }

        grouped[assignee].total++;
        grouped[assignee].statusCount[status] = (grouped[assignee].statusCount[status] || 0) + 1;
      });

      const summaryList = Object.entries(grouped).map(([name, data]) => ({
        id: `summary-${name}`,
        message: `${name} has ${data.total} active leads`,
        statusBreakdown: data.statusCount,
        createdAt: new Date().toISOString(),
        type: 'summary'
      }));

      setSummaryNotifications(summaryList);
    } catch (err) {
      console.error("Notification error:", err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        setError('Session expired. Please login again.');
      } else {
        setError(err.response?.data?.message || err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [isToday]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!showDropdown) return;

    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Calculate unread count
  const unreadCount = [
    ...todayNotifications.filter(n => !viewedNotifications.has(n.id)),
    ...summaryNotifications.filter(n => !viewedNotifications.has(n.id))
  ].length;

  const getStatusIcon = (status) => {
    switch(status) {
      case 'new':
        return <FaExclamationCircle className="status-icon new" />;
      case 'contacted':
        return <FaInfoCircle className="status-icon contacted" />;
      default:
        return <FaUser className="status-icon default" />;
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark this notification as viewed
    setViewedNotifications(prev => new Set([...prev, notification.id]));
    
    // Navigate to viewlist component
    navigate('/view-list');
    
    // Close the notification dropdown
    setShowDropdown(false);
  };

  return (
    <div className="notification-container">
      <button 
        className={`notification-icon ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
      >
        <FaBell />
        {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
      </button>

      {showDropdown && (
        <div className="notification-dropdown" ref={dropdownRef}>
          <div className="dropdown-header">
            <h3>Lead Notifications</h3>
            {error && <div className="error-banner">{error}</div>}
          </div>

          <div className="notification-list">
            {loading ? (
              <div className="loading">Loading notifications...</div>
            ) : (
              <>
                {/* Today's Leads Section */}
                <div className="section-title">
                  <FaCalendarAlt className="section-icon" />
                  Today's Leads
                </div>
                
                {todayNotifications.length > 0 ? (
                  todayNotifications.map(notification => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${viewedNotifications.has(notification.id) ? 'viewed' : 'unviewed'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon-wrapper">
                        {getStatusIcon(notification.status)}
                      </div>
                      <div className="notification-content">
                        <p className="notification-message">{notification.message}</p>
                        <small className="notification-time">
                          {new Date(notification.createdAt).toLocaleTimeString()}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No leads created today</div>
                )}

                {/* User Summary Section */}
                <div className="section-title">
                  <FaUser className="section-icon" />
                  Team Summary
                </div>
                
                {summaryNotifications.length > 0 ? (
                  summaryNotifications.map(notification => (
                    <div 
                      key={notification.id}
                      className={`notification-item ${viewedNotifications.has(notification.id) ? 'viewed' : 'unviewed'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="notification-icon-wrapper">
                        <FaUser className="status-icon summary" />
                      </div>
                      <div className="notification-content">
                        <p className="notification-message">{notification.message}</p>
                        <div className="status-summary-container">
                          {Object.entries(notification.statusBreakdown).map(([status, count]) => (
                            <span key={status} className="status-badge">
                              {status}: {count}
                            </span>
                          ))}
                        </div>
                        <small className="notification-time">
                          Updated: {new Date(notification.createdAt).toLocaleTimeString()}
                        </small>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No active leads in your team</div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notification;